import type {
  BusinessCredential,
  BusinessLogoContentType,
  BusinessProfile,
  BusinessProfileInput,
  WorkspaceCollaborator,
} from "../services/naraDoor";

const K = "businessProfiles.error";
export const MAX_CREDENTIALS = 10;
export const MAX_LOGO_BYTES = 1024 * 1024;
const LOGO_TYPES: readonly string[] = ["image/png", "image/jpeg"];
const CONTACT_ROLES: readonly string[] = ["owner", "admin", "editor", "viewer"];

export interface ContactCandidate {
  userId: string;
  name: string;
}

/** Active seats that can be a main contact, by first and last name. The email is never an option label. */
export function contactCandidates(collaborators: WorkspaceCollaborator[]): ContactCandidate[] {
  return collaborators
    .filter((c) => (!c.status || c.status === "active") && CONTACT_ROLES.includes(c.role))
    .map((c) => ({ userId: c.user_id, name: `${c.user?.first_name ?? ""} ${c.user?.last_name ?? ""}`.trim() }))
    .filter((c) => c.name);
}

export type ProfileAction = "load" | "save" | "delete" | "logoUpload";

/** i18n key for a failed call: the server's `details.reason` first, then the action's own sentence. */
export function profileErrorKey(err: unknown, action: ProfileAction): string {
  const reason = errorReason(err);
  if (reason === "name_taken") return `${K}.nameTaken`;
  if (reason === "contact") return `${K}.contact`;
  if (reason === "logo") return `${K}.logoFile`;
  return `${K}.${action}`;
}

const errorReason = (err: unknown): unknown => (err as { details?: { reason?: unknown } } | null)?.details?.reason;

/** Ids and the server reason only: a profile field is business data and stays out of the log. */
export function logProfileError(call: string, ids: Record<string, string | undefined>, err: unknown): void {
  console.error(`${call} failed`, { ...ids, reason: typeof errorReason(err) === "string" ? errorReason(err) : undefined });
}

/** Every field as the form edits it: text only, so an input never holds null. */
export interface ProfileDraft {
  name: string;
  legal_name: string;
  trade_name: string;
  address_line1: string;
  address_line2: string;
  city: string;
  region: string;
  postal_code: string;
  country: string;
  phone: string;
  email: string;
  website: string;
  main_contact_user_id: string;
  main_contact_title: string;
  neq: string;
  gst_number: string;
  qst_number: string;
  credentials: BusinessCredential[];
  is_default: boolean;
}

export const emptyDraft = (): ProfileDraft => ({
  name: "",
  legal_name: "",
  trade_name: "",
  address_line1: "",
  address_line2: "",
  city: "",
  region: "",
  postal_code: "",
  country: "CA",
  phone: "",
  email: "",
  website: "",
  main_contact_user_id: "",
  main_contact_title: "",
  neq: "",
  gst_number: "",
  qst_number: "",
  credentials: [],
  is_default: false,
});

export const draftFromProfile = (p: BusinessProfile): ProfileDraft => ({
  name: p.name,
  legal_name: p.legal_name,
  trade_name: p.trade_name ?? "",
  address_line1: p.address_line1 ?? "",
  address_line2: p.address_line2 ?? "",
  city: p.city ?? "",
  region: p.region ?? "",
  postal_code: p.postal_code ?? "",
  country: p.country,
  phone: p.phone ?? "",
  email: p.email ?? "",
  website: p.website ?? "",
  main_contact_user_id: p.main_contact_user_id ?? "",
  main_contact_title: p.main_contact_title ?? "",
  neq: p.neq ?? "",
  gst_number: p.gst_number ?? "",
  qst_number: p.qst_number ?? "",
  credentials: p.credentials.map((c) => ({ ...c })),
  is_default: p.is_default,
});

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const WEB_ADDRESS = /^https?:\/\/\S+$/i;

/** Client checks, as i18n keys by field. The server validates again. */
export function draftErrors(d: ProfileDraft): Partial<Record<"legal_name" | "email" | "website", string>> {
  const errors: Partial<Record<"legal_name" | "email" | "website", string>> = {};
  if (!d.legal_name.trim()) errors.legal_name = `${K}.legalNameRequired`;
  if (d.email.trim() && !EMAIL.test(d.email.trim())) errors.email = `${K}.email`;
  if (d.website.trim() && !WEB_ADDRESS.test(d.website.trim())) errors.website = `${K}.website`;
  return errors;
}

const orNull = (v: string): string | null => v.trim() || null;

export function draftToInput(d: ProfileDraft): BusinessProfileInput {
  return {
    // The label is optional in the form (no copy for "required"): fall back to the legal name.
    name: (d.name.trim() || d.legal_name.trim()).slice(0, 100),
    legal_name: d.legal_name.trim(),
    trade_name: orNull(d.trade_name),
    address_line1: orNull(d.address_line1),
    address_line2: orNull(d.address_line2),
    city: orNull(d.city),
    region: orNull(d.region),
    postal_code: orNull(d.postal_code),
    country: d.country.trim().toUpperCase(),
    phone: orNull(d.phone),
    email: orNull(d.email),
    website: orNull(d.website),
    main_contact_user_id: d.main_contact_user_id || null,
    main_contact_title: orNull(d.main_contact_title),
    neq: orNull(d.neq),
    gst_number: orNull(d.gst_number),
    qst_number: orNull(d.qst_number),
    credentials: d.credentials
      .map((c) => ({ label: c.label.trim(), number: c.number.trim() }))
      .filter((c) => c.label || c.number),
  };
}

/** i18n key when the file cannot be a logo (PNG or JPEG, 1 MB at most); null when it can. */
export function logoFileProblem(file: Pick<File, "type" | "size">): string | null {
  return LOGO_TYPES.includes(file.type) && file.size <= MAX_LOGO_BYTES ? null : `${K}.logoFile`;
}

/** The signed URL only accepts the content type it was signed for. */
export async function putLogo(uploadUrl: string, body: Blob, contentType: BusinessLogoContentType): Promise<void> {
  const res = await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": contentType }, body });
  if (!res.ok) throw new Error(`Logo upload failed: HTTP ${res.status}`);
}
